import { faHome } from "@fortawesome/free-solid-svg-icons";
import { faRefresh } from "@fortawesome/free-solid-svg-icons/faRefresh";
import * as Sentry from "@sentry/react";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { useAppSelector } from "../redux/store";
import { userSliceSelectors } from "../redux/user/user.selector";
import { Breakpoints } from "../theme/Breakpoints";
import { Spacing } from "../theme/Spacing";
import { AtiraEmpty } from "./AtiraEmpty";
import { Button } from "./Button";
import { Flex } from "./Flex";
import { Text } from "./Text";

const Container = styled(Flex)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: ${Spacing.m};
`;

const Description = styled(Text)`
  font-weight: bold;
  text-align: center;

  @media (min-width: ${Breakpoints.TABLET}) {
    max-width: 35rem;
  }
`;

const HomeButton = styled(Button)`
  background-color: transparent;
  border: ${({ theme }) => `1px solid ${theme.main}`};
  color: ${({ theme }) => theme.main};
  width: 15rem;
  margin: 0;
  font-size: ${Spacing.m};

  &:hover {
    background-color: ${({ theme }) => theme.main};
    color: ${({ theme }) => theme.sub};
  }
`;

const RefreshButton = styled(Button)`
  width: 15rem;
  margin: 0;
  font-size: ${Spacing.m};
`;

type Props = {
  error: unknown;
  resetError: () => void;
};

export const ErroBoundary: React.FC<Props> = ({ error, resetError }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isLoggedIn = useAppSelector(userSliceSelectors.selectLoggedInUserId);

  const onBackHome = () => {
    if (isLoggedIn) {
      navigate("/dashboard");
    } else {
      navigate("/");
    }
  };

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <Container>
      <Flex flexDirection="column" gap="m" alignItems="center">
        <AtiraEmpty description={""} />

        <Description>{t("common.error.back_home")}</Description>

        <Flex gap="m" flexWrap="wrap" justifyContent="center" marginTop="l">
          <HomeButton onClick={onBackHome} icon={faHome}>
            {t("common.goHome")}
          </HomeButton>

          <RefreshButton onClick={resetError} icon={faRefresh} hover>
            {t("common.refresh")}
          </RefreshButton>
        </Flex>
      </Flex>
    </Container>
  );
};
